import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../api';

/**
 * TutorAssignmentDropdown
 *
 * Admin-only dropdown for assigning/reassigning tutors to students.
 * Shows loading spinner during API call and handles errors gracefully.
 *
 * Props:
 * - studentUuid: string - Student's UUID
 * - currentTutorId: number|null - Currently assigned tutor ID
 * - tutors: array - List of available tutors [{id, name}]
 * - onAssignment: function - Callback after successful assignment (studentUuid, tutorId, tutorName)
 */
const TutorAssignmentDropdown = ({
  studentUuid,
  currentTutorId,
  tutors = [],
  onAssignment
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(currentTutorId || '');

  useEffect(() => {
    setSelectedValue(currentTutorId || '');
  }, [currentTutorId]);

  const handleChange = async (e) => {
    const newValue = e.target.value;
    const tutorId = newValue === '' ? null : parseInt(newValue, 10);

    // Don't do anything if selecting the same tutor
    if (tutorId === currentTutorId) {
      return;
    }

    setIsLoading(true);
    setSelectedValue(newValue);

    try {
      const response = await apiFetch(`/api/students/${studentUuid}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tutor_id: tutorId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to assign tutor');
      }

      const result = await response.json();

      // Call the success callback
      if (onAssignment) {
        onAssignment(studentUuid, result.tutor_id, result.tutor_name);
      }
    } catch (error) {
      console.error('Error assigning tutor:', error);
      // Revert to previous value on error
      setSelectedValue(currentTutorId || '');
      // Could add a toast notification here in the future
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`roster-table__tutor-dropdown ${isLoading ? 'roster-table__tutor-dropdown--loading' : ''}`}>
      <select
        value={selectedValue}
        onChange={handleChange}
        disabled={isLoading}
        aria-label="Assign tutor"
      >
        <option value="">Unassigned</option>
        {tutors.map(tutor => (
          <option key={tutor.id} value={tutor.id}>
            {tutor.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default TutorAssignmentDropdown;
